/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import {
    CircleAlert,
    CircleCheckBig,
    CircleX,
    Clock3,
    Info,
    Minus,
    PiggyBank,
    Tag,
    TrendingDown,
    TrendingUp,
    type LucideIcon,
} from 'lucide-react';

import { useI18n } from '@/context/I18nContext';
import type {
    ElectricityPriceAssessment,
    ElectricityPriceLevel,
    ElectricityPriceRecommendation,
    ElectricityPriceRecommendationAction,
    ElectricityPriceRecommendationReason,
    ElectricityPriceTrafficLight,
    ElectricityPriceTrendDirection,
} from '@/lib/electricity/ElectricityPriceAdvisor';

interface ElectricityPriceRecommendationPanelProps {
    conversion: number;
    maxWaitHours: number;
    recommendation: ElectricityPriceRecommendation | null;
    referenceTime: Date;
    timeline: ElectricityPriceAssessment[];
    unit: string;
    updatedAt: Date | null;
}

interface TrafficLightPresentation {
    className: string;
    icon: LucideIcon;
    label: string;
    textClassName: string;
}

function ElectricityPriceRecommendationPanel({
    conversion,
    maxWaitHours,
    recommendation,
    referenceTime,
    timeline,
    unit,
    updatedAt,
}: ElectricityPriceRecommendationPanelProps) {
    const { locale, t } = useI18n();
    const numberFormatter = new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    });
    const slopeFormatter = new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        signDisplay: 'exceptZero',
    });
    const formattedUpdateDate = updatedAt?.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
    const formattedUpdateTime = updatedAt?.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
    });

    if (!recommendation) {
        return (
            <div className="flex h-full w-full flex-col items-start justify-center gap-2 p-5 text-left">
                <h2 className="text-lg font-semibold">{t('Electricity price recommendation')}</h2>
                <p className="text-sm text-base-content/65">
                    <span className="font-semibold">{t('Can be scheduled later')}:</span>{' '}
                    {t('up to {hours} hours', { hours: maxWaitHours })}
                </p>
                <p className="text-sm text-base-content/65">
                    {t('A recommendation will appear as soon as current price data is available.')}
                </p>
            </div>
        );
    }

    const trafficLight = getTrafficLightPresentation(
        recommendation.recommendation.trafficLight,
        t,
    );
    const TrafficLightIcon = trafficLight.icon;
    const TrendIcon = getTrendIcon(recommendation.trend.direction);
    const recommendationTitle = getActionLabel(recommendation.recommendation.action, t);
    const recommendationReason = getReasonText(
        recommendation.recommendation.reason,
        recommendation.bestOption.timestamp,
        referenceTime,
        locale,
        t,
    );
    const trendLabel = getTrendLabel(recommendation.trend.direction, t);
    const currentTimestampTooltip = formatTimestampTooltip(
        recommendation.current.timestamp,
        locale,
    );
    const bestOptionTimestampTooltip = formatTimestampTooltip(
        recommendation.bestOption.timestamp,
        locale,
    );
    const trendTimestampTooltip = formatTimestampRangeTooltip(
        recommendation.current.timestamp,
        timeline[timeline.length - 1]?.timestamp ?? recommendation.current.timestamp,
        locale,
    );
    const formatPrice = (priceEurPerMwh: number) => (
        `${numberFormatter.format(priceEurPerMwh * conversion)} ${unit}`
    );
    const formatPriceValue = (priceEurPerMwh: number) => (
        numberFormatter.format(priceEurPerMwh * conversion)
    );

    return (
        <div className="flex h-full min-h-0 w-full flex-col items-stretch gap-4 overflow-y-auto p-5 text-left">
            <div className="grid gap-x-5 gap-y-1 xl:grid-cols-[minmax(0,1.35fr)_minmax(17rem,0.85fr)]">
                <h2 className="text-lg font-semibold xl:col-start-1 xl:row-start-1">
                    {t('Electricity price recommendation')}
                </h2>

                <section className="min-w-0 xl:col-start-1 xl:row-start-2">
                    <p className="text-sm text-base-content/65">
                        {t('Recommendation for the current time (for the next {hours} hours)', {
                            hours: formatHours(maxWaitHours, locale),
                        })}
                    </p>

                    <div className="mt-5 flex items-start gap-4">
                        <div
                            className={`flex size-24 shrink-0 items-center justify-center rounded-box border ${trafficLight.className}`}
                            role="status"
                            aria-label={`${trafficLight.label} – ${recommendationTitle}`}
                        >
                            <TrafficLightIcon className="h-12 w-12" aria-hidden="true" />
                        </div>
                        <div className="min-w-0 pt-1">
                            <h3 className={`text-3xl font-bold ${trafficLight.textClassName}`}>
                                {recommendationTitle}
                            </h3>
                            <div
                                className="tooltip tooltip-top mt-2 w-fit cursor-help text-xl text-base-content/75"
                                data-tip={bestOptionTimestampTooltip}
                            >
                                {recommendationReason}
                            </div>
                        </div>
                    </div>

                    <div className="mt-5 flex items-start gap-3 text-xl sm:ml-28">
                        <Clock3 className="mt-0.5 h-6 w-6 shrink-0" aria-hidden="true" />
                        <div className="space-y-1">
                            <p>
                                <span className="font-semibold">{t('Best consumption time')}:</span>{' '}
                                <span
                                    className="tooltip tooltip-top cursor-help"
                                    data-tip={bestOptionTimestampTooltip}
                                >
                                    {formatTimelineTimeDifference(
                                        recommendation.bestOption.timestamp,
                                        referenceTime,
                                        locale,
                                        t,
                                    )}
                                </span>
                            </p>
                            <p className="text-base-content/65">
                                <span className="font-semibold">{t('Expected price')}:</span>{' '}
                                {formatPrice(recommendation.bestOption.priceEurPerMwh)}
                            </p>
                        </div>
                    </div>
                </section>

                <div className="grid content-start overflow-hidden rounded-box border border-base-300 max-xl:mt-5 xl:col-start-2 xl:row-start-2">
                    <RecommendationMetric
                        connected
                        icon={Tag}
                        label={t('Current price')}
                        value={formatPrice(recommendation.current.priceEurPerMwh)}
                        tooltip={currentTimestampTooltip}
                        valueBadgeLevel={recommendation.current.level}
                    />
                    <RecommendationMetric
                        className="border-t border-base-300"
                        connected
                        icon={TrendIcon}
                        label={t('Trend (next {hours} hours)', {
                            hours: formatHours(maxWaitHours, locale),
                        })}
                        value={trendLabel}
                        detail={`${slopeFormatter.format(
                            recommendation.trend.slopeEurPerMwhPerHour * conversion,
                        )} ${unit}/${t('hour')}`}
                        tooltip={trendTimestampTooltip}
                    />
                    <RecommendationMetric
                        className="border-t border-base-300"
                        connected
                        icon={PiggyBank}
                        label={t('Potential savings')}
                        value={formatPrice(recommendation.bestOption.savingsEurPerMwh)}
                        detail={t('within {hours} hours', {
                            hours: formatHours(recommendation.horizon.availableHours, locale),
                        })}
                        tooltip={bestOptionTimestampTooltip}
                    />
                </div>
            </div>

            {recommendation.horizon.availableHours < recommendation.horizon.maxWaitHours ? (
                <p className="text-xs text-base-content/55">
                    {t('Price data is currently available for {available} of {requested} requested hours.', {
                        available: formatHours(recommendation.horizon.availableHours, locale),
                        requested: formatHours(recommendation.horizon.maxWaitHours, locale),
                    })}
                </p>
            ) : null}

            <div className="border-t border-base-content/20" />

            <div>
                <h2 className="text-lg font-semibold">
                    {t('Prices and recommendations over time (for the next {hours} hours)', {
                        hours: formatHours(maxWaitHours, locale),
                    })}
                </h2>
                <div className="-mt-10 grid auto-cols-[minmax(13rem,1fr)] grid-flow-col gap-3 overflow-x-auto pt-10 pb-2">
                    {timeline.map((assessment, index) => (
                        <TimelineItem
                            key={assessment.timestamp.getTime()}
                            assessment={assessment}
                            formatPriceValue={formatPriceValue}
                            referenceTime={referenceTime}
                            tooltipAlignment={index === 0
                                ? 'start'
                                : index === timeline.length - 1
                                    ? 'end'
                                    : 'center'}
                            unit={unit}
                        />
                    ))}
                </div>
            </div>

            <div
                className="status--implausible flex flex-col items-start justify-between gap-3 rounded-box p-4 text-left sm:flex-row sm:items-center"
                role="note"
            >
                <div className="flex items-center gap-3">
                    <Info className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span>
                        {t('Day-ahead prices may still change. All information is provided without guarantee.')}
                    </span>
                </div>
                <div className="ml-auto flex w-full shrink-0 items-center justify-end gap-2 text-right sm:w-auto">
                    <Clock3 className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span>
                        {updatedAt && formattedUpdateDate && formattedUpdateTime
                            ? t('Updated: {date} at {time}', {
                                date: formattedUpdateDate,
                                time: formattedUpdateTime,
                            })
                            : t('Updated: Not available')}
                    </span>
                </div>
            </div>
        </div>
    );
}

function RecommendationMetric({
    className,
    connected = false,
    detail,
    icon: Icon,
    label,
    tooltip,
    value,
    valueBadgeLevel,
}: {
    className?: string;
    connected?: boolean;
    detail?: string;
    icon: LucideIcon;
    label: string;
    tooltip: string;
    value: string;
    valueBadgeLevel?: ElectricityPriceLevel;
}) {
    return (
        <div className={`flex items-start gap-3 bg-base-200/45 p-3 ${
            connected ? '' : 'rounded-box border border-base-300'
        } ${className ?? ''}`}>
            <div className="flex size-12 shrink-0 items-center justify-center rounded-full border border-base-content/20 bg-base-100/50">
                <Icon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div className="min-w-0">
                <div
                    className="tooltip tooltip-top w-fit cursor-help text-xs font-semibold text-base-content/65"
                    data-tip={tooltip}
                >
                    {label}
                </div>
                <div className="mt-1">
                    {valueBadgeLevel ? (
                        <PriceLevelBadge level={valueBadgeLevel} value={value} />
                    ) : (
                        <p className="text-sm font-semibold">{value}</p>
                    )}
                </div>
                {detail ? <p className="mt-0.5 text-xs text-base-content/55">{detail}</p> : null}
            </div>
        </div>
    );
}

function TimelineItem({
    assessment,
    formatPriceValue,
    referenceTime,
    tooltipAlignment,
    unit,
}: {
    assessment: ElectricityPriceAssessment;
    formatPriceValue: (priceEurPerMwh: number) => string;
    referenceTime: Date;
    tooltipAlignment: 'center' | 'end' | 'start';
    unit: string;
}) {
    const { locale, t } = useI18n();
    const trafficLight = getTrafficLightPresentation(assessment.trafficLight, t);
    const TrendIcon = getTrendIcon(assessment.trend);
    const AssessmentIcon = trafficLight.icon;

    return (
        <article
            className={`rounded-box p-4 text-center ${getTimelineStatusClassName(assessment.trafficLight)}`}
            aria-label={trafficLight.label}
        >
            <div>
                <h3
                    className={`tooltip tooltip-top mx-auto w-fit cursor-help text-base font-semibold ${
                        tooltipAlignment === 'start'
                            ? 'tooltip-start'
                            : tooltipAlignment === 'end'
                                ? 'tooltip-end'
                                : ''
                    }`}
                    data-tip={formatTimestampTooltip(assessment.timestamp, locale)}
                >
                    {formatTimelineTimeDifference(
                        assessment.timestamp,
                        referenceTime,
                        locale,
                        t,
                    )}
                </h3>
                <p className="mt-0.5 text-xs text-base-content/60">
                    {formatTimelineSlot(assessment.timestamp, locale)}
                </p>
            </div>

            <div className={`mt-3 flex items-baseline justify-center gap-1 ${
                getPriceLevelTextClassName(assessment.priceLevel)
            }`}>
                <span className="text-3xl font-bold">
                    {formatPriceValue(assessment.priceEurPerMwh)}
                </span>
                <span className="text-sm font-semibold">{unit}</span>
            </div>

            <div className="my-3 border-t border-base-content/20" />

            <div className="flex items-center justify-center gap-2 text-sm">
                <TrendIcon className="h-4 w-4" aria-hidden="true" />
                <span>{getTrendLabel(assessment.trend, t)}</span>
            </div>
            <p className="mt-2 font-semibold">
                {getTimelineRecommendationLabel(assessment.action, t)}
            </p>

            <div className="mt-3 flex items-start gap-3 rounded-box border border-base-content/20 bg-base-100/35 p-3 text-left">
                <AssessmentIcon
                    className={`h-6 w-6 shrink-0 ${trafficLight.textClassName}`}
                    aria-hidden="true"
                />
                <div className="min-w-0">
                    <p className="text-sm font-semibold">
                        {getTimelineActionLabel(assessment.action, t)}
                    </p>
                    <p className="text-xs text-base-content/65">
                        {getTimelinePriceOpportunityLabel(assessment, t)}
                    </p>
                </div>
            </div>
        </article>
    );
}

function PriceLevelBadge({
    level,
    value,
}: {
    level: ElectricityPriceLevel;
    value: string;
}) {
    const { t } = useI18n();
    const classNames: Record<ElectricityPriceLevel, string> = {
        cheap: 'badge-success',
        normal: 'badge-warning',
        expensive: 'badge-error',
    };

    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{value}</span>
            <span className={`badge badge-soft badge-sm whitespace-nowrap text-xs font-semibold ${classNames[level]}`}>
                {getPriceLevelLabel(level, t)}
            </span>
        </div>
    );
}

function formatHours(hours: number, locale: string): string {
    return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 1,
    }).format(hours);
}

function formatTimelineTimeDifference(
    timestamp: Date,
    referenceTime: Date,
    locale: string,
    t: (key: string, values?: Record<string, number | string>) => string,
): string {
    const differenceMinutes = Math.round(
        (timestamp.getTime() - referenceTime.getTime()) / (60 * 1000),
    );

    if (differenceMinutes === 0) {
        return t('Now');
    }

    if (differenceMinutes < 0) {
        const elapsedMinutes = Math.abs(differenceMinutes);

        return elapsedMinutes === 1
            ? t('One minute ago')
            : t('{minutes} minutes ago', { minutes: elapsedMinutes });
    }

    if (differenceMinutes === 1) {
        return t('In one minute');
    }

    if (differenceMinutes < 60) {
        return t('In {minutes} minutes', { minutes: differenceMinutes });
    }

    if (differenceMinutes >= 3 * 60) {
        const time = new Intl.DateTimeFormat(locale, {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Europe/Berlin',
        }).format(timestamp);

        return t('From {time}', { time });
    }

    const hours = Math.floor(differenceMinutes / 60);
    const minutes = String(differenceMinutes % 60).padStart(2, '0');

    return t('In {hours}:{minutes} hours', { hours, minutes });
}

function formatTimelineSlot(timestamp: Date, locale: string): string {
    const formatter = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Berlin',
    });

    return `${formatter.format(timestamp)} – ${formatter.format(
        new Date(timestamp.getTime() + 60 * 60 * 1000),
    )}`;
}

function formatTimestampTooltip(timestamp: Date, locale: string): string {
    return new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'Europe/Berlin',
    }).format(timestamp);
}

function formatTimestampRangeTooltip(from: Date, to: Date, locale: string): string {
    const formatter = new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'Europe/Berlin',
    });

    return `${formatter.format(from)} – ${formatter.format(to)}`;
}

function getActionLabel(
    action: ElectricityPriceRecommendationAction,
    t: (key: string) => string,
): string {
    const labels: Record<ElectricityPriceRecommendationAction, string> = {
        'consume-now': 'Now is a good time',
        'consume-soon': 'Use soon if needed',
        neutral: 'Consider current needs',
        wait: 'Wait if possible',
        avoid: 'Avoid consumption if possible',
    };

    return t(labels[action]);
}

function getReasonText(
    reason: ElectricityPriceRecommendationReason,
    bestOptionTimestamp: Date,
    referenceTime: Date,
    locale: string,
    t: (key: string, values?: Record<string, number | string>) => string,
): string {
    const time = formatTimelineTimeDifference(bestOptionTimestamp, referenceTime, locale, t);

    if (reason === 'price-cheap-cheaper-soon') {
        return t('Optional waiting: Electricity is expected to be cheaper {time}.', {
            time,
        });
    }

    if (reason === 'cheaper-soon' || reason === 'much-cheaper-soon') {
        return t(
            reason === 'much-cheaper-soon'
                ? 'Electricity is expected to be significantly cheaper {time}.'
                : 'Electricity is expected to be cheaper {time}.',
            { time },
        );
    }

    const labels: Record<Exclude<
        ElectricityPriceRecommendationReason,
        'cheaper-soon' | 'much-cheaper-soon' | 'price-cheap-cheaper-soon'
    >, string> = {
        'price-cheap': 'The current price is favourable and no relevant additional savings are expected.',
        'price-expensive': 'The current price is expensive. Avoid flexible consumption if possible.',
        'prices-rising': 'Prices are rising. The current slot is more attractive than the following hours.',
        'best-price-now': 'The current slot is the best available option within the selected period.',
        'no-significant-benefit': 'No relevant savings are expected from waiting within the selected period.',
    };

    return t(labels[reason]);
}

function getPriceLevelLabel(
    level: ElectricityPriceLevel,
    t: (key: string) => string,
): string {
    const labels: Record<ElectricityPriceLevel, string> = {
        cheap: 'Favourable',
        normal: 'Normal',
        expensive: 'Expensive',
    };

    return t(labels[level]);
}

function getTimelineActionLabel(
    action: ElectricityPriceRecommendationAction,
    t: (key: string) => string,
): string {
    const labels: Record<ElectricityPriceRecommendationAction, string> = {
        'consume-now': 'Consume',
        'consume-soon': 'Use soon',
        neutral: 'Keep waiting',
        wait: 'Wait',
        avoid: 'Avoid',
    };

    return t(labels[action]);
}

function getTimelineRecommendationLabel(
    action: ElectricityPriceRecommendationAction,
    t: (key: string) => string,
): string {
    const labels: Record<ElectricityPriceRecommendationAction, string> = {
        'consume-now': 'Consume electricity',
        'consume-soon': 'Consume electricity soon',
        neutral: 'Consider waiting',
        wait: 'Waiting is worthwhile',
        avoid: 'Wait if possible',
    };

    return t(labels[action]);
}

function getTimelinePriceOpportunityLabel(
    assessment: ElectricityPriceAssessment,
    t: (key: string) => string,
): string {
    if (assessment.priceLevel === 'cheap') {
        return t('Favourable');
    }

    if (assessment.priceLevel === 'expensive') {
        return t('Unfavourable');
    }

    if (assessment.trafficLight === 'green') {
        return t('Best available price');
    }

    if (assessment.trend === 'falling') {
        return t('Favourable soon');
    }

    return t('Not yet optimal');
}

function getPriceLevelTextClassName(level: ElectricityPriceLevel): string {
    const classNames: Record<ElectricityPriceLevel, string> = {
        cheap: 'text-success',
        normal: 'text-warning',
        expensive: 'text-error',
    };

    return classNames[level];
}

function getTimelineStatusClassName(trafficLight: ElectricityPriceTrafficLight): string {
    const classNames: Record<ElectricityPriceTrafficLight, string> = {
        green: 'status--ok',
        yellow: 'status--warning',
        red: 'status--error',
    };

    return classNames[trafficLight];
}

function getTrafficLightPresentation(
    trafficLight: ElectricityPriceTrafficLight,
    t: (key: string) => string,
): TrafficLightPresentation {
    const presentations: Record<ElectricityPriceTrafficLight, TrafficLightPresentation> = {
        green: {
            className: 'border-success/40 bg-success/10 text-success',
            icon: CircleCheckBig,
            label: t('Green – good time for flexible consumption'),
            textClassName: 'text-success',
        },
        yellow: {
            className: 'border-warning/40 bg-warning/10 text-warning',
            icon: CircleAlert,
            label: t('Yellow – weigh up current needs'),
            textClassName: 'text-warning',
        },
        red: {
            className: 'border-error/40 bg-error/10 text-error',
            icon: CircleX,
            label: t('Red – wait if possible'),
            textClassName: 'text-error',
        },
    };

    return presentations[trafficLight];
}

function getTrendIcon(direction: ElectricityPriceTrendDirection): LucideIcon {
    if (direction === 'falling') {
        return TrendingDown;
    }

    if (direction === 'rising') {
        return TrendingUp;
    }

    return Minus;
}

function getTrendLabel(
    direction: ElectricityPriceTrendDirection,
    t: (key: string) => string,
): string {
    const labels: Record<ElectricityPriceTrendDirection, string> = {
        falling: 'Falling',
        stable: 'Stable',
        rising: 'Rising',
    };

    return t(labels[direction]);
}

export default ElectricityPriceRecommendationPanel;
